import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { isEmpty, isNil } from "ramda";

const LineChart = ({
  prefix,
  data,
  environmentalData,
  measureName,
  withToolbox = false,
  resetZoomHandler = () => {},
  pointsSelectHandler = () => {},
  startZoom = 0,
  endZoom = 100,
  startIndex,
  endIndex,
  isAlert,
  alertMeasure,
  afterAlert,
  setAfterAlert,
  alertLines,
  setAlertLines,
  setLoadingAverage,
  setLoadingAverageTemporal,
  axisKeys,
  subData,
  info,
  intl,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  useEffect(() => {
    let lastRatio = window.devicePixelRatio;

    const interval = setInterval(() => {
      if (window.devicePixelRatio !== lastRatio) {
        lastRatio = window.devicePixelRatio;
        chartInstanceRef.current?.resize();
      }
    }, 300);

    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const chartDom = chartRef.current;
    if (!isNil(chartDom)) {
      const myChart = echarts.init(chartDom);
      chartInstanceRef.current = myChart;

      // Aquí deberías poner tus opciones del gráfico
      // myChart.setOption({...});

      // Observa cambios de tamaño del contenedor, incluso por zoom
      const resizeObserver = new ResizeObserver(() => {
        myChart.resize();
      });

      resizeObserver.observe(chartDom);

      return () => {
        resizeObserver.disconnect();
        myChart.dispose();
      };
    }
  }, []);

  const dateOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  const colors = [
    "#5470C6", // Azul fuerte
    "#91CC75", // Verde suave
    "#EE6666", // Rojo intenso
    "#9A60B4", // Púrpura
    "#FAC858", // Amarillo dorado
    "#EA7CCC", // Rosa claro
    "#FC8452", // Naranja
    "#73C0DE", // Azul cielo
    "#3BA272", // Verde bosque
    "#FF5733", // Naranja rojizo
    "#33FF57", // Verde brillante
    "#5733FF", // Azul eléctrico
    "#FFD700", // Oro
    "#40E0D0", // Turquesa
    "#8A2BE2", // Violeta
    "#FF69B4", // Rosa fuerte
    "#7FFF00", // Chartreuse
    "#DC143C", // Carmesí
    "#00CED1", // Azul oscuro
    "#4682B4", // Acero azul
    "#32CD32", // Verde lima
    "#FF4500", // Naranja rojizo
    "#9400D3", // Violeta oscuro
    "#20B2AA", // Verde aguamarina
    "#FFDAB9", // Melocotón
    "#B22222", // Rojo ladrillo
    "#00FA9A", // Verde primavera
    "#191970", // Azul medianoche
    "#ADFF2F", // Verde amarillo
  ];

  useEffect(() => {
    const chartElement = document.getElementById(`${prefix}-stacked-line-chart`);
    const myChart = echarts.init(chartElement);
    let minDataDepth = Number.POSITIVE_INFINITY;
    let maxDataDepth = Number.NEGATIVE_INFINITY;
    let minData = Number.POSITIVE_INFINITY;
    let maxData = Number.NEGATIVE_INFINITY;
    let minEnvironmentalData = []; //Number.POSITIVE_INFINITY;
    let maxEnvironmentalData = []; //Number.NEGATIVE_INFINITY;
    let subs = false;
    let depth = null;
    data?.series?.forEach((series) => {
      if (series.substructures) {
        subs = series.substructures;
        series?.axis_keys?.forEach((key) => {
          depth = series?.data?.[key].values;
          series?.data?.[key]?.depth?.forEach((value) => {
            if (!isNil(data) && !contieneTemporaEnParentesis(data.title)) {
              if (value < minDataDepth) minDataDepth = value;
              if (value > maxDataDepth) maxDataDepth = value;
            } else {
              if (value[1] < minDataDepth) minDataDepth = value[1];
              if (value[1] > maxDataDepth) maxDataDepth = value[1];
            }
          });
          series?.data?.[key]?.values?.forEach((value) => {
            if (!isNil(data) && !contieneTemporaEnParentesis(data.title)) {
              if (value < minData) minData = value;
              if (value > maxData) maxData = value;
            } else {
              if (value[1] < minData) minData = value[1];
              if (value[1] > maxData) maxData = value[1];
            }
          });
        });
      } else {
        series?.data?.forEach((value) => {
          if (!isNil(data) && !contieneTemporaEnParentesis(data.title)) {
            if (value < minData) minData = value;
            if (value > maxData) maxData = value;
          } else {
            if (value[1] < minData) minData = value[1];
            if (value[1] > maxData) maxData = value[1];
          }
        });
      }
    });
    environmentalData?.series?.forEach((series, index) => {
      minEnvironmentalData.push(Number.POSITIVE_INFINITY);
      maxEnvironmentalData.push(Number.NEGATIVE_INFINITY);
      series?.data?.forEach((value) => {
        if (!isNil(data) && !contieneTemporaEnParentesis(data.title)) {
          if (value < minEnvironmentalData[index]) minEnvironmentalData[index] = Math.floor(value * 100) / 100;
          if (value > maxEnvironmentalData[index]) maxEnvironmentalData[index] = Math.ceil(value * 100) / 100;
        } else {
          if (value[1] < minEnvironmentalData[index]) minEnvironmentalData[index] = Math.floor(value[1] * 100) / 100;
          if (value[1] > maxEnvironmentalData[index]) maxEnvironmentalData[index] = Math.ceil(value[1] * 100) / 100;
        }
      });
    });
    const xAxisData =
      (data?.xAxis?.data?.length || 0) > (environmentalData?.xAxis?.data?.length || 0) ? data : environmentalData || {};

    let combinado = null;
    if (!isNil(data) && !isNil(environmentalData) && contieneTemporaEnParentesis(data.title)) {
      combinado = data?.series[0].data.concat(environmentalData?.series[0].data);
      combinado.sort((a, b) => new Date(a[0]) - new Date(b[0]));
      combinado = combinado.map((subArray) => subArray[0]);
    }
    const dataMinRounded = Math.floor(minData * 100) / 100;
    const dataMaxRounded = Math.ceil(maxData * 100) / 100;

    const colors = [
      "#5470C6", // Azul fuerte
      "#91CC75", // Verde suave
      "#EE6666", // Rojo intenso
      "#9A60B4", // Púrpura
      "#FAC858", // Amarillo dorado
      "#EA7CCC", // Rosa claro
      "#FC8452", // Naranja
      "#73C0DE", // Azul cielo
      "#3BA272", // Verde bosque
      "#FF5733", // Naranja rojizo
      "#33FF57", // Verde brillante
      "#5733FF", // Azul eléctrico
      "#FFD700", // Oro
      "#40E0D0", // Turquesa
      "#8A2BE2", // Violeta
      "#FF69B4", // Rosa fuerte
      "#7FFF00", // Chartreuse
      "#DC143C", // Carmesí
      "#00CED1", // Azul oscuro
      "#4682B4", // Acero azul
      "#32CD32", // Verde lima
      "#FF4500", // Naranja rojizo
      "#9400D3", // Violeta oscuro
      "#20B2AA", // Verde aguamarina
      "#FFDAB9", // Melocotón
      "#B22222", // Rojo ladrillo
      "#00FA9A", // Verde primavera
      "#191970", // Azul medianoche
      "#ADFF2F", // Verde amarillo
    ];
    const legend = [];
    if (contieneTemporaEnParentesis(data.title)) {
      for (let index = 0; index < data?.series.length; index++) {
        legend.push(intl.formatMessage({ id: data.series[index].title }));
      }
      for (let index = 0; index < environmentalData?.series.length; index++) {
        legend.push(
          environmentalData.series[index].title
            .split("_") // Divide la cadena en partes donde hay '_'
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitaliza la primera letra de cada palabra
            .join(" "),
        );
      }
    }
    if (data?.series?.length > 1 && !contieneTemporaEnParentesis(data.title)) {
      for (let serieIndex = 0; serieIndex < data?.series?.length; serieIndex++) {
        for (let indexAxe = 0; indexAxe < data?.series[serieIndex]?.axis_keys?.length; indexAxe++) {
          legend.push(`${data?.series[serieIndex]?.axis_keys[indexAxe]} ${data?.series[serieIndex]?.name}`);
        }
      }
    }
    const options = {
      color: colors,
      animation: false,
      tooltip:
        !contieneTemporaEnParentesis(data.title) && !subs
          ? {
              trigger: "axis",
              position: (pt) => [pt?.[0], "10%"],
            }
          : null,
      legend: subs
        ? !contieneTemporaEnParentesis(data.title)
          ? {
              data: !isEmpty(legend) ? legend : data.legend,
              top: data?.series?.length > 3 ? "7%" : "0%",
              type: "scroll",
            }
          : { data: legend, top: data?.series?.length > 3 ? "7%" : "0%", type: "scroll" }
        : !contieneTemporaEnParentesis(data.title)
        ? { data: !isEmpty(legend) ? legend : data.legend, top: data?.series?.length > 3 ? "7%" : "0%", type: "scroll" }
        : { data: legend, top: data?.series?.length > 3 ? "7%" : "0%", type: "scroll" },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "7%",
        top: legend.length > 4 ? "25%" : "15%",
        containLabel: true,
      },
      toolbox: withToolbox
        ? {
            top: legend.length > 4 ? "12%" : null,
            feature: {
              dataZoom: {
                yAxisIndex: "all",
                xAxisIndex: "all",
                brushStyle: {
                  color: "#5470C655",
                  borderType: "solid",
                  borderWidth: 1,
                  borderColor: "#5470C6",
                  shadowColor: "#5470C6",
                },
                title: {
                  zoom: intl.formatMessage({ id: "Zoom in" }),
                  back: intl.formatMessage({ id: "Zoom out" }),
                },
              },
              restore: {
                title: intl.formatMessage({ id: "Restore zoom" }),
              },
              saveAsImage: {
                title: intl.formatMessage({ id: "Save as image" }),
              },
              myCustomCSVDownload: {
                show: true,
                title: intl.formatMessage({ id: "Download CSV" }),
                icon: "image://csv-icon80.png",
                onclick: function () {
                  !isNil(environmentalData) && !isEmpty(environmentalData) ? downloadTemporalCSV() : downloadCSV();
                },
              },
            },
          }
        : {},
      xAxis: {
        type: !contieneTemporaEnParentesis(data.title) ? "value" : "time",
        min: !contieneTemporaEnParentesis(data.title) ? minData : null,
        max: !contieneTemporaEnParentesis(data.title) ? maxData : null, //ESTO HACE QUE EN TEMPORAL NO SE VEA NADA
        data: subs
          ? !contieneTemporaEnParentesis(data.title)
            ? depth.map((num) => Number(num.toFixed(2)))
            : !isNil(combinado)
            ? combinado.map((date) => {
                return new Date(date).toLocaleString("es-ES", dateOptions);
              })
            : null
          : !contieneTemporaEnParentesis(data.title)
          ? xAxisData?.xAxis?.data.map((num) => {
              return typeof num === "number" ? parseFloat(num.toFixed(1)) : num;
            })
          : !isNil(combinado)
          ? combinado.map((date) => {
              return new Date(date).toLocaleString("es-ES", dateOptions);
            })
          : xAxisData?.xAxis?.data.map((date) => {
              return new Date(date);
            }),
        boundaryGap: false,
        splitLine: {
          show: false,
        },
        axisLine: { onZero: false }, //Fix the bug
        name: subs
          ? !contieneTemporaEnParentesis(data.title)
            ? `${intl.formatMessage({ id: "temperature" })} (Cº)`
            : `${intl.formatMessage({ id: "time" })}`
          : !contieneTemporaEnParentesis(data.title)
          ? "PK (m)"
          : `${intl.formatMessage({ id: "time" })}`,
        nameLocation: "middle", // Esto centrará el nombre en el eje
        nameGap: 25,
      },
      yAxis: subs
        ? !contieneTemporaEnParentesis(data.title) || isNil(environmentalData)
          ? {
              type: "value",
              min: minDataDepth === Infinity ? null : -maxDataDepth,
              max: maxDataDepth === -Infinity ? null : 0,
              name:
                measureName === "temperature" || prefix.includes("temperature")
                  ? `${intl.formatMessage({ id: "depth" })} (m)`
                  : measureName === "strain" || prefix.includes("strain")
                  ? `${intl.formatMessage({ id: "strain" })} (μ)`
                  : null,
              nameRotate: 270,
              nameLocation: "middle",
              nameGap: 35,
            }
          : [
              {
                type: "value",
                min: dataMinRounded === Infinity ? null : dataMinRounded,
                max: dataMaxRounded === -Infinity ? null : dataMaxRounded,
                name:
                  measureName === "temperature" || prefix.includes("temperature")
                    ? `${intl.formatMessage({ id: "temperature" })} (Cº)`
                    : measureName === "strain" || prefix.includes("strain")
                    ? `${intl.formatMessage({ id: "strain" })} (μ)`
                    : null,
                nameRotate: 90,
                nameLocation: "middle",
                nameGap: 35,
                axisLine: {
                  show: true,
                  lineStyle: {
                    color: colors[0],
                  },
                },
              },
              ...environmentalData?.series?.map((series, index) => ({
                type: "value",
                min: minEnvironmentalData[index] === Infinity ? null : minEnvironmentalData[index],
                max: maxEnvironmentalData[index] === -Infinity ? null : maxEnvironmentalData[index],
                name: series.title
                  .split("_") // Divide la cadena en partes donde hay '_'
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitaliza la primera letra de cada palabra
                  .join(" "),
                nameRotate: 270,
                nameLocation: "middle",
                offset: 55 * index,
                nameGap: 35,
                axisLine: {
                  show: true,
                  lineStyle: {
                    color: colors[index + data?.series?.length],
                  },
                },
              })),
            ]
        : !contieneTemporaEnParentesis(data.title) || isNil(environmentalData)
        ? {
            type: "value",
            min: dataMinRounded === Infinity ? null : dataMinRounded,
            max: dataMaxRounded === -Infinity ? null : dataMaxRounded,
            name:
              measureName === "temperature" || prefix.includes("temperature")
                ? `${intl.formatMessage({ id: "temperature" })} (Cº)`
                : measureName === "strain" || prefix.includes("strain")
                ? `${intl.formatMessage({ id: "strain" })} (μ)`
                : null,
            nameRotate: 90,
            nameLocation: "middle",
            nameGap: 35,
          }
        : [
            {
              type: "value",
              min: dataMinRounded === Infinity ? null : dataMinRounded,
              max: dataMaxRounded === -Infinity ? null : dataMaxRounded,
              name:
                measureName === "temperature" || prefix.includes("temperature")
                  ? `${intl.formatMessage({ id: "temperature" })} (Cº)`
                  : measureName === "strain" || prefix.includes("strain")
                  ? `${intl.formatMessage({ id: "strain" })} (μ)`
                  : null,
              nameRotate: 90,
              nameLocation: "middle",
              nameGap: 35,
              axisLine: {
                show: true,
                lineStyle: {
                  color: colors[0],
                },
              },
            },
            ...environmentalData?.series?.map((series, index) => ({
              type: "value",
              min: minEnvironmentalData[index] === Infinity ? null : minEnvironmentalData[index],
              max: maxEnvironmentalData[index] === -Infinity ? null : maxEnvironmentalData[index],
              name: series.title
                .split("_") // Divide la cadena en partes donde hay '_'
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitaliza la primera letra de cada palabra
                .join(" "),
              nameRotate: 270,
              nameLocation: "middle",
              offset: 55 * index,
              nameGap: 35,
              axisLine: {
                show: true,
                lineStyle: {
                  color: colors[index + data?.series?.length],
                },
              },
            })),
          ],
      series: subs
        ? !contieneTemporaEnParentesis(data.title)
          ? data?.series?.flatMap((series, seriesIndex) => {
              const offset = seriesIndex * series.axis_keys.length;
              return series.axis_keys.map((key, keyIndex) => ({
                data: series.data[key].depth.map((value, depthIndex) => [series.data[key].values[depthIndex], -value]),
                color: colors[keyIndex + offset],
                name: !isEmpty(legend) ? legend[keyIndex + offset] : key,
                title: !isEmpty(legend) ? legend[keyIndex + offset] : key,
                type: "line",
                symbolSize: 1,
                markLine: { data: [] }, // Asegura que markLine esté presente
              }));
            })
          : [
              ...(data?.series?.map((s) => ({
                ...s,
                name: intl.formatMessage({ id: s.title }),
                yAxisIndex: 0,
              })) || []),
              ...(environmentalData?.series?.map((s, index) => ({
                ...s,
                name: s.title
                  .split("_") // Divide la cadena en partes donde hay '_'
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitaliza la primera letra de cada palabra
                  .join(" "),
                type: "line",
                symbolSize: 1,
                yAxisIndex: index + 1,
                color: colors[index + data?.series?.length],
              })) || []),
            ]
        : !contieneTemporaEnParentesis(data.title)
        ? data?.series?.map((s) => ({
            ...s,
          }))
        : [
            ...(data?.series?.map((s) => ({
              ...s,
              name: intl.formatMessage({ id: s.title }),
              yAxisIndex: 0,
            })) || []),
            ...(environmentalData?.series?.map((s, index) => ({
              ...s,
              name: s.title
                .split("_") // Divide la cadena en partes donde hay '_'
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitaliza la primera letra de cada palabra
                .join(" "),
              type: "line",
              symbolSize: 1,
              yAxisIndex: 1, // forzar segundo eje
              color: colors[index + data?.series?.length],
            })) || []),
          ],
    };
    myChart.setOption(options, { notMerge: false, lazyUpdate: true, silent: true });
    if (!isNil(alertLines) && alertLines.start !== null && alertLines.end !== null) {
      const zoomLines = [
        {
          name: "Zoom Start",
          xAxis: alertLines.start,
          lineStyle: {
            color: "red",
            width: 2,
            type: "solid",
          },
          label: { show: false },
        },
        {
          name: "Zoom End",
          xAxis: alertLines.end,
          lineStyle: {
            color: "red",
            width: 2,
            type: "solid",
          },
          label: { show: false },
        },
      ];

      myChart.setOption(
        {
          series:
            data?.series?.map((s) => ({
              ...s,
              type: "line",
              markLine: {
                symbol: "none",
                data: zoomLines,
              },
            })) || [], // Asegúrate de que sea un array vacío si no hay series
        },
        { notMerge: false, lazyUpdate: true, silent: true },
      );
    }
    const addZoomLines = (startValue, endValue) => {
      const zoomLines = [
        {
          name: "Zoom Start",
          yAxis: !isNil(subData) && subData.type === "inclinometer" ? startValue : null,
          xAxis: !isNil(subData) && subData.type === "polyline" ? startValue : null,
          lineStyle: {
            color: "red",
            width: 2,
            type: "solid",
          },
          label: { show: false },
        },
        {
          name: "Zoom End",
          yAxis: !isNil(subData) && subData.type === "inclinometer" ? endValue : null,
          xAxis: !isNil(subData) && subData.type === "polyline" ? endValue : null,
          lineStyle: {
            color: "red",
            width: 2,
            type: "solid",
          },
          label: { show: false },
        },
      ];

      myChart.setOption(
        {
          series: subs
            ? data?.series?.flatMap((series, seriesIndex) => {
                const offset = seriesIndex * series.axis_keys.length;
                return series.axis_keys.map((key, keyIndex) => ({
                  data: series.data[key].depth.map((value, depthIndex) => [
                    series.data[key].values[depthIndex],
                    -value,
                  ]),
                  color: colors[keyIndex + offset],
                  name: !isEmpty(legend) ? legend[keyIndex + offset] : key,
                  title: !isEmpty(legend) ? legend[keyIndex + offset] : key,
                  type: "line",
                  symbolSize: 1,
                  markLine: {
                    symbol: "none",
                    data: zoomLines,
                  }, // Asegura que markLine esté presente
                }));
              })
            : data?.series?.map((s) => ({
                ...s,
                markLine: {
                  symbol: "none",
                  data: zoomLines,
                },
              })),
        },
        { notMerge: false, lazyUpdate: true, silent: true },
      );
    };

    myChart.on("click", (params) => {
      onChartClick(params);
      let i = -Infinity;
      let j = Infinity;
      addZoomLines(i, j);
    });
    myChart.on("dataZoom", (params) => {
      if (
        params &&
        params.batch &&
        params.batch.length > 0 &&
        !isNil(params.batch[1].startValue) &&
        !isNil(params.batch[1].endValue)
      ) {
        let i = subs ? params.batch[1].startValue : params.batch[0].startValue;
        let j = subs ? params.batch[1].endValue : params.batch[0].endValue;
        let realI = j > 0 ? j : indexNumeroMasCercano(data?.series?.[0]?.data?.[axisKeys[0]].depth, Math.abs(i));
        let realJ = j > 0 ? i : indexNumeroMasCercano(data?.series?.[0]?.data?.[axisKeys[0]].depth, Math.abs(j));
        if (realI - realJ >= 64) {
          let centerPoint = parseInt((realI - realJ) / 2) + realJ;
          realJ = centerPoint >= 32 ? centerPoint - 32 : 0;
          centerPoint += 32;
          const count = !isNil(axisKeys)
            ? data?.series?.[0]?.data?.[axisKeys[0]].values.length
            : data?.series?.[0]?.data?.length ?? 0 ?? 0;
          realI = centerPoint < count ? centerPoint : count - 1;
        }
        if (!contieneTemporaEnParentesis(data.title)) {
          if (j > 0) {
            addZoomLines(realJ, realI);
          } else {
            addZoomLines(
              -data?.series?.[0]?.data?.[axisKeys[0]].depth[realJ],
              -data?.series?.[0]?.data?.[axisKeys[0]].depth[realI],
            );
          }
        }
        onDataZoom(realI, realJ, data.series[0].data.length);
      }
    });
    myChart.on("restore", () => {
      resetZoomHandler();
    });
    myChart.on("mousewheel", function (params) {
      params.event.preventDefault(); // Evita el zoom con la rueda del ratón
    });

    return () => {
      setLoadingAverage(false);
      setLoadingAverageTemporal(false);
      myChart.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, environmentalData, prefix, startZoom, endZoom]);

  useEffect(() => {
    if (!isNil(startIndex) && !isNil(endIndex) && isAlert && !(measureName === "strain" && alertMeasure === "leak")) {
      const chartElement = document.getElementById(`${prefix}-stacked-line-chart`);
      const myChart = echarts.init(chartElement);
      let i = startIndex;
      let j = endIndex;
      let realI = j > 0 ? j : indexNumeroMasCercano(data?.series?.[0]?.data?.[axisKeys[0]].depth, Math.abs(i));
      let realJ = j > 0 ? i : indexNumeroMasCercano(data?.series?.[0]?.data?.[axisKeys[0]].depth, Math.abs(j));
      if (realI - realJ >= 64) {
        let centerPoint = parseInt((realI - realJ) / 2) + realJ;
        realJ = centerPoint >= 32 ? centerPoint - 32 : 0;
        centerPoint += 32;
        const count = !isNil(axisKeys)
          ? data?.series?.[0]?.data?.[axisKeys[0]].values.length
          : data?.series?.[0]?.data?.length ?? 0 ?? 0;
        realI = centerPoint < count ? centerPoint : count - 1;
      }
      if (!contieneTemporaEnParentesis(data.title)) {
        const zoomLines = [
          {
            name: "Zoom Start",
            yAxis:
              !isNil(subData) && subData.type === "inclinometer"
                ? -data?.series?.[0]?.data?.[axisKeys[0]].depth[realJ]
                : null,
            xAxis: !isNil(subData) && subData.type === "polyline" ? realJ : null,
            lineStyle: {
              color: "red",
              width: 2,
              type: "solid",
            },
            label: { show: false },
          },
          {
            name: "Zoom End",
            yAxis:
              !isNil(subData) && subData.type === "inclinometer"
                ? -data?.series?.[0]?.data?.[axisKeys[0]].depth[realI]
                : null,
            xAxis: !isNil(subData) && subData.type === "polyline" ? realI : null,
            lineStyle: {
              color: "red",
              width: 2,
              type: "solid",
            },
            label: { show: false },
          },
        ];
        setAlertLines({ start: realJ, end: realI });

        const iniZoom = Math.floor((startIndex * 100) / data.xAxis.data.length) - 1;
        const finZoom = Math.floor((endIndex * 100) / data.xAxis.data.length) + 2;
        const legend = [];
        if (data?.series?.length > 1 && !contieneTemporaEnParentesis(data.title)) {
          for (let serieIndex = 0; serieIndex < data?.series?.length; serieIndex++) {
            for (let indexAxe = 0; indexAxe < data?.series[serieIndex]?.axis_keys?.length; indexAxe++) {
              legend.push(`${data?.series[serieIndex]?.axis_keys[indexAxe]} ${data?.series[serieIndex]?.name}`);
            }
          }
        }
        myChart.setOption(
          {
            series: !isNil(axisKeys)
              ? data?.series?.flatMap((series, seriesIndex) => {
                  const offset = seriesIndex * series.axis_keys.length;
                  return series.axis_keys.map((key, keyIndex) => ({
                    data: series.data[key].depth.map((value, depthIndex) => [
                      series.data[key].values[depthIndex],
                      -value,
                    ]),
                    color: colors[keyIndex + offset],
                    name: !isEmpty(legend) ? legend[keyIndex + offset] : key,
                    title: !isEmpty(legend) ? legend[keyIndex + offset] : key,
                    type: "line",
                    symbolSize: 1,
                    markLine: {
                      symbol: "none",
                      data: zoomLines,
                    }, // Asegura que markLine esté presente
                  }));
                })
              : data?.series?.map((s) => ({
                  ...s,
                  markLine: {
                    symbol: "none",
                    data: zoomLines,
                  },
                })),
            dataZoom: [
              {
                type: "inside",
                start: iniZoom,
                end: finZoom,
              },
            ],
          },
          { notMerge: false, lazyUpdate: true, silent: true },
        );
        if (!afterAlert && !isNil(alertMeasure)) pointsSelectHandler(startIndex, endIndex, false);
        if (isNil(alertMeasure) && !afterAlert) pointsSelectHandler(startIndex, endIndex, afterAlert);
      }
    } else if (!isNil(startIndex) && !isNil(endIndex) && isAlert) {
      if (!afterAlert) pointsSelectHandler(startIndex, endIndex, false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndex]);
  function isFloat(value) {
    return !isNaN(value) && value.toString().indexOf(".") !== -1;
  }

  function downloadCSV() {
    //asset, equipment, channel, dateTime/PK
    const header = [
      info.asset,
      info.equipment.name,
      info.equipment.channelsNames[info.channel - 1],
      isFloat(data?.series?.[0]?.name) || Number.isInteger(data?.series?.[0]?.name)
        ? data?.series?.[0]?.name.toFixed(2)
        : info.dateTime,
    ].join(",");
    const subHeader = [
      isFloat(data?.series?.[0]?.name) || Number.isInteger(data?.series?.[0]?.name)
        ? intl.formatMessage({ id: "date" })
        : "PK",
      intl.formatMessage({ id: measureName }),
    ].join(",");
    let dateTime, combinedData;
    if (isFloat(data?.series?.[0]?.name) || Number.isInteger(data?.series?.[0]?.name)) {
      dateTime = data?.series?.[0]?.name.toFixed(2);
      const timeData = data?.xAxis?.data;
      const serialData = data?.series?.[0]?.data;
      combinedData = timeData?.map((date, index) => [date.replace(" ", "T"), serialData[index][1].toFixed(2)]);
    } else {
      dateTime = data?.series?.[0]?.name?.replace(" ", "T");
      const pkData = data?.xAxis?.data;
      const serialData = data?.series?.[0]?.data;
      combinedData = pkData?.map((pk, index) => [pk.toFixed(2), serialData[index].toFixed(2)]);
    }
    const csvData = [header, subHeader, ...combinedData].join("\n");

    const blob = new Blob([csvData], { type: "text/csv" });

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${dateTime}_${measureName}.csv`;

    a.click();

    URL.revokeObjectURL(a.href);
  }
  function downloadTemporalCSV() {
    const header = [
      info.asset,
      info.equipment.name,
      info.equipment.channelsNames[info.channel - 1],
      data?.series?.[0]?.name.toFixed(2),
    ].join(",");
    const subHeader = [intl.formatMessage({ id: "date" }), intl.formatMessage({ id: measureName })].join(",");
    let dateTime, combinedData;
    dateTime = data?.series?.[0]?.name;
    const timeData = data?.xAxis?.data;
    const serialData = data?.series?.[0]?.data;
    combinedData = timeData?.map((date, index) => [date.replace(" ", "T"), serialData[index][1].toFixed(2)]);

    const csvData = [header, subHeader, ...combinedData].join("\n");

    const blob = new Blob([csvData], { type: "text/csv" });

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${dateTime.toFixed(2)}_${measureName}.csv`;

    a.click();

    URL.revokeObjectURL(a.href);
    for (let envIndex = 0; envIndex < environmentalData?.series?.length; envIndex++) {
      const subHeaderEnv = [intl.formatMessage({ id: "date" }), environmentalData.series[envIndex].title].join(",");
      dateTime = data?.series?.[0]?.name;
      const timeData = data?.xAxis?.data;
      const serialData = environmentalData?.series?.[envIndex]?.data;
      combinedData = timeData?.map((date, index) => [date.replace(" ", "T"), serialData[index][1].toFixed(2)]);
      const envCsvData = [header, subHeaderEnv, ...combinedData].join("\n");

      const blob = new Blob([envCsvData], { type: "text/csv" });

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${environmentalData.series[envIndex].title}_${dateTime}_${intl.formatMessage({
        id: "intensity",
      })}.csv`;

      a.click();

      URL.revokeObjectURL(a.href);
    }
  }
  const onChartClick = (params) => {
    if (params.componentType === "series") {
      const dataIndex = params.dataIndex;
      pointsSelectHandler(dataIndex, dataIndex, afterAlert);
    }
  };
  function contieneTemporaEnParentesis(cadena) {
    const regexParentesis = /\(.*temporal.*\)/i;

    return regexParentesis.test(cadena);
  }

  const onDataZoom = (realI, realJ, length) => {
    //if (
    //  !isNil(params?.batch) &&
    //  !isEmpty(params.batch.length) &&
    //  !isNil(params.batch?.[0]?.startValue) &&
    //  !isNil(params.batch?.[0]?.endValue)
    //) {
    //const startIndex2 = params.batch[0].startValue;
    //const endIndex2 = params.batch[0].endValue;
    pointsSelectHandler(realJ, realI, afterAlert);
    //}
  };

  function indexNumeroMasCercano(array, objetivo) {
    return array.reduce(
      (indiceCercano, num, i) =>
        Math.abs(num - objetivo) < Math.abs(array[indiceCercano] - objetivo) ? i : indiceCercano,
      0,
    );
  }
  return (
    <div
      id={`${prefix}-stacked-line-chart`}
      style={{ width: "90%", height: !contieneTemporaEnParentesis(data.title) ? "110%" : "275%" }}
    />
  );
};

export default LineChart;
